import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLang } from '../i18n/context';
import MarketPriceChart from '../components/MarketPriceChart';
import './CryptoDetailPage.css';

const COINGECKO_IDS = {
  bitcoin: { symbol: 'BTC', name: 'Bitcoin' },
  ethereum: { name: 'Ethereum', symbol: 'ETH' },
  solana: { name: 'Solana', symbol: 'SOL' },
  ripple: { name: 'XRP', symbol: 'XRP' },
  dogecoin: { name: 'Dogecoin', symbol: 'DOGE' },
  cardano: { name: 'Cardano', symbol: 'ADA' },
  'avalanche-2': { name: 'Avalanche', symbol: 'AVAX' },
  binancecoin: { name: 'BNB', symbol: 'BNB' },
  'pax-gold': { name: 'PAX Gold', symbol: 'PAXG' },
};

const TRADINGVIEW_SYMBOLS = {
  bitcoin: 'BTCUSDT',
  ethereum: 'ETHUSDT',
  solana: 'SOLUSDT',
  ripple: 'XRPUSDT',
  dogecoin: 'DOGEUSDT',
  cardano: 'ADAUSDT',
  'avalanche-2': 'AVAXUSDT',
  binancecoin: 'BNBUSDT',
  'pax-gold': 'PAXGUSDT',
};

function formatNumber(num) {
  if (!num && num !== 0) return '—';
  if (num >= 1e12) return '$' + (num / 1e12).toFixed(2) + 'T';
  if (num >= 1e9) return '$' + (num / 1e9).toFixed(2) + 'B';
  if (num >= 1e6) return '$' + (num / 1e6).toFixed(2) + 'M';
  return '$' + num.toLocaleString();
}

function formatSupply(num) {
  if (!num) return '—';
  if (num >= 1e9) return (num / 1e9).toFixed(2) + 'B';
  if (num >= 1e6) return (num / 1e6).toFixed(2) + 'M';
  if (num >= 1e3) return (num / 1e3).toFixed(2) + 'K';
  return num.toLocaleString();
}

function formatPrice(price) {
  if (price == null) return '—';
  if (price >= 1) return '$' + price.toLocaleString(undefined, { maximumFractionDigits: 2 });
  if (price >= 0.01) return '$' + price.toFixed(4);
  return '$' + price.toFixed(6);
}

function getChangeClass(val) {
  if (val == null) return '';
  return val >= 0 ? ' up' : ' down';
}

function getFngColor(val) {
  if (val <= 25) return '#ef4444';
  if (val <= 45) return '#f97316';
  if (val <= 55) return '#eab308';
  if (val <= 75) return '#84cc16';
  return '#22c55e';
}

function formatChange(val) {
  if (val == null) return '—';
  return (val >= 0 ? '▲ ' : '▼ ') + Math.abs(val).toFixed(2) + '%';
}

export default function CryptoDetailPage() {
  const { symbol } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const [data, setData] = useState(null);
  const [fng, setFng] = useState(null);
  const [dominance, setDominance] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [chartLoading, setChartLoading] = useState(true);
  const [chartError, setChartError] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const coinMeta = COINGECKO_IDS[symbol];

  const fetchExtras = useCallback(() => {
    fetch('/api/market/fear-greed')
      .then(r => r.json())
      .then(d => {
        const f = d?.data;
        if (f) setFng({ value: parseInt(f.value, 10), classification: f.value_classification });
      })
      .catch(() => {});

    if (symbol === 'bitcoin') {
      setDominance(null);
    }
  }, [symbol]);

  useEffect(() => {
    if (!coinMeta) {
      setError(true);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(false);

    const cacheKey = `crypto_detail_${symbol}`;
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        if (Date.now() - parsed.ts < 120000) {
          setData(parsed.data);
          setLoading(false);
          fetchExtras();
          return;
        }
      } catch { /* ignore */ }
    }

    fetch(`/api/market/coin?id=${encodeURIComponent(symbol)}`)
      .then((r) => {
        if (!r.ok) throw new Error();
        return r.json();
      })
      .then((json) => {
        setData(json);
        sessionStorage.setItem(cacheKey, JSON.stringify({ data: json, ts: Date.now() }));
        setLoading(false);
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });

    fetchExtras();
  }, [symbol, coinMeta, fetchExtras]);

  useEffect(() => {
    if (!coinMeta) return undefined;
    let cancelled = false;
    const tvSymbol = TRADINGVIEW_SYMBOLS[symbol] || 'BTCUSDT';

    setChartLoading(true);
    setChartError('');
    fetch(`/api/market/history?symbol=${tvSymbol}&interval=1d&limit=180`)
      .then((r) => {
        if (!r.ok) throw new Error();
        return r.json();
      })
      .then((payload) => {
        if (cancelled) return;
        const points = (payload?.candles || payload?.data || payload?.prices || [])
          .map((point) => ({
            time: point.time,
            value: Number(point.value ?? point.close ?? point.price),
          }))
          .filter((point) => point.time && Number.isFinite(point.value));
        setChartData(points);
      })
      .catch(() => {
        if (!cancelled) {
          setChartData([]);
          setChartError(lang === 'zh' ? '价格图表暂时不可用' : 'Price chart unavailable');
        }
      })
      .finally(() => {
        if (!cancelled) setChartLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [symbol, coinMeta, lang]);

  if (loading) {
    return (
      <div className="crypto-detail-page">
        <div className="crypto-detail-loading">{t('home.cryptoDetailLoading')}</div>
      </div>
    );
  }

  if (error || !coinMeta || !data) {
    return (
      <div className="crypto-detail-page">
        <button className="crypto-detail-back" onClick={() => navigate(-1)}>{t('home.cryptoDetailBack')}</button>
        <div className="crypto-detail-not-found">{t('home.cryptoDetailNotFound')}</div>
      </div>
    );
  }

  const md = data.market_data || {};
  const price = md.current_price?.usd;
  const change24h = md.price_change_percentage_24h;
  const marketCap = md.market_cap?.usd;
  const circulating = md.circulating_supply;
  const totalSupply = md.total_supply;
  const maxSupply = md.max_supply;
  const vol24h = md.total_volume?.usd;
  const high24h = md.high_24h?.usd;
  const low24h = md.low_24h?.usd;
  const ath = md.ath?.usd;
  const athChange = md.ath_change_percentage?.usd;
  const atl = md.atl?.usd;
  const atlChange = md.atl_change_percentage?.usd;
  const image = data.image?.large || data.image?.small;
  const rank = data.market_cap_rank;

  const fngColor = fng ? getFngColor(fng.value) : null;

  const tvSymbol = TRADINGVIEW_SYMBOLS[symbol] || 'BTCUSDT';

  return (
    <div className="crypto-detail-page">
      <button className="crypto-detail-back" onClick={() => navigate(-1)}>{t('home.cryptoDetailBack')}</button>

      {/* 币种头部 */}
      <div className="crypto-detail-header">
        {image ? (
          <img src={image} alt={data.name} className="crypto-detail-icon-img" />
        ) : (
          <div className={`crypto-detail-icon${getChangeClass(change24h)}`}>
            {coinMeta.symbol.charAt(0)}
          </div>
        )}
        <div className="crypto-detail-header-info">
          <div className="crypto-detail-header-name">
            {data.name} <span className="crypto-detail-header-symbol">{coinMeta.symbol}/USDT</span>
          </div>
          <div className="crypto-detail-header-sub">
            {lang === 'zh' ? '加密货币' : 'Cryptocurrency'} · {lang === 'zh' ? '市值排名' : 'Rank'} #{rank || '—'} · 24h {lang === 'zh' ? '成交额' : 'Volume'} {formatNumber(vol24h)}
          </div>
        </div>
        <div className="crypto-detail-header-price-wrap">
          <div className="crypto-detail-price">{formatPrice(price)}</div>
          <div className={`crypto-detail-change${getChangeClass(change24h)}`}>
            {formatChange(change24h)}
          </div>
        </div>
      </div>

      {/* 核心指标 4宫格 */}
      <div className="crypto-detail-metrics">
        <div className="crypto-detail-metric-card">
          <div className="crypto-detail-metric-label">{t('home.cryptoDetailMarketCap')}</div>
          <div className="crypto-detail-metric-value accent">{formatNumber(marketCap)}</div>
        </div>
        <div className="crypto-detail-metric-card">
          <div className="crypto-detail-metric-label">{t('home.cryptoDetailCirculating')}</div>
          <div className="crypto-detail-metric-value">
            {formatSupply(circulating)} {coinMeta.symbol}
          </div>
        </div>
        <div className="crypto-detail-metric-card">
          <div className="crypto-detail-metric-label">{t('home.cryptoDetailFng')}</div>
          <div className="crypto-detail-metric-value accent" style={fng ? { color: fngColor } : {}}>
            {fng ? fng.value : '—'}
          </div>
        </div>
        <div className="crypto-detail-metric-card">
          <div className="crypto-detail-metric-label">{t('home.cryptoDetailDominance')}</div>
          <div className="crypto-detail-metric-value">
            {dominance ? dominance.toFixed(1) + '%' : (symbol === 'bitcoin' ? '—' : '—')}
          </div>
        </div>
      </div>

      <div className="crypto-detail-body">
        <div className="crypto-detail-main">
          {/* 价格走势 */}
          <div className="crypto-detail-card">
            <div className="crypto-detail-card-top">
              <div className="crypto-detail-card-title">{t('home.cryptoDetailPriceTrend')}</div>
            </div>
            <div className="crypto-detail-chart-wrap">
              <MarketPriceChart
                data={chartData}
                tone="crypto"
                loading={chartLoading}
                error={chartError}
                label={`${tvSymbol} ${t('home.cryptoDetailPriceTrend')}`}
              />
            </div>
          </div>

          {/* 市场数据详情 */}
          <div className="crypto-detail-card">
            <div className="crypto-detail-card-title" style={{ marginBottom: '1rem' }}>
              {lang === 'zh' ? '市场数据' : 'Market Data'}
            </div>
            <div className="crypto-detail-data-grid">
              <div className="crypto-detail-data-item">
                <span className="crypto-detail-data-label">24h {lang === 'zh' ? '最高' : 'High'}</span>
                <span className="crypto-detail-data-value">{formatPrice(high24h)}</span>
              </div>
              <div className="crypto-detail-data-item">
                <span className="crypto-detail-data-label">24h {lang === 'zh' ? '最低' : 'Low'}</span>
                <span className="crypto-detail-data-value">{formatPrice(low24h)}</span>
              </div>
              <div className="crypto-detail-data-item">
                <span className="crypto-detail-data-label">{lang === 'zh' ? '历史最高' : 'ATH'}</span>
                <span className="crypto-detail-data-value">{formatPrice(ath)}</span>
              </div>
              <div className="crypto-detail-data-item">
                <span className="crypto-detail-data-label">{lang === 'zh' ? '距ATH' : 'From ATH'}</span>
                <span className={`crypto-detail-data-value${getChangeClass(athChange)}`}>{athChange != null ? athChange.toFixed(1) + '%' : '—'}</span>
              </div>
              <div className="crypto-detail-data-item">
                <span className="crypto-detail-data-label">{lang === 'zh' ? '历史最低' : 'ATL'}</span>
                <span className="crypto-detail-data-value">{formatPrice(atl)}</span>
              </div>
              <div className="crypto-detail-data-item">
                <span className="crypto-detail-data-label">{lang === 'zh' ? '距ATL' : 'From ATL'}</span>
                <span className={`crypto-detail-data-value${getChangeClass(atlChange)}`}>{atlChange != null ? atlChange.toFixed(1) + '%' : '—'}</span>
              </div>
              <div className="crypto-detail-data-item">
                <span className="crypto-detail-data-label">{lang === 'zh' ? '总供给量' : 'Total Supply'}</span>
                <span className="crypto-detail-data-value">{totalSupply ? formatSupply(totalSupply) : '∞'}</span>
              </div>
              <div className="crypto-detail-data-item">
                <span className="crypto-detail-data-label">{lang === 'zh' ? '最大供给' : 'Max Supply'}</span>
                <span className="crypto-detail-data-value">{maxSupply ? formatSupply(maxSupply) : '∞'}</span>
              </div>
            </div>
          </div>

          {/* 工具快捷入口 */}
          <div className="crypto-detail-tools">
            <div className="crypto-detail-tool-card" onClick={() => navigate('/pattern-match')}>
              <div className="crypto-detail-tool-icon">📊</div>
              <div className="crypto-detail-tool-name">K{lang === 'zh' ? '线形态' : 'line Pattern'}</div>
            </div>
            <div className="crypto-detail-tool-card" onClick={() => navigate('/support-resistance')}>
              <div className="crypto-detail-tool-icon">📈</div>
              <div className="crypto-detail-tool-name">{lang === 'zh' ? '支撑压力位' : 'Support/Resistance'}</div>
            </div>
            <div className="crypto-detail-tool-card" onClick={() => navigate('/dump-detective')}>
              <div className="crypto-detail-tool-icon">🔍</div>
              <div className="crypto-detail-tool-name">{lang === 'zh' ? '出货侦探' : 'Dump Detective'}</div>
            </div>
          </div>
        </div>

        {/* 右侧栏 */}
        <div className="crypto-detail-sidebar">
          <div className="crypto-detail-sidebar-box">
            <div className="crypto-detail-sidebar-title">{t('home.cryptoDetailOnchain')}</div>
            <div className="crypto-detail-sidebar-rows">
              <div className="crypto-detail-sidebar-row">
                <span className="label">{t('home.cryptoDetailExchangeInflow')}</span>
                <span className="value">—</span>
              </div>
              <div className="crypto-detail-sidebar-row">
                <span className="label">{t('home.cryptoDetailMinerSell')}</span>
                <span className="value">—</span>
              </div>
              <div className="crypto-detail-sidebar-row">
                <span className="label">{t('home.cryptoDetailNvt')}</span>
                <span className="value">—</span>
              </div>
              <div className="crypto-detail-sidebar-row">
                <span className="label">{t('home.cryptoDetailMvrv')}</span>
                <span className="value">—</span>
              </div>
            </div>
          </div>
          <div className="crypto-detail-sidebar-box">
            <div className="crypto-detail-sidebar-title">{t('home.cryptoDetailWhaleSignal')}</div>
            <div className="crypto-detail-whale-status normal">{t('home.cryptoDetailWhaleNormal')}</div>
            <div className="crypto-detail-sidebar-link" onClick={() => navigate('/dump-detective')}>
              {t('home.cryptoDetailWhaleViewDetail')}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
